import type { ExportSummary } from '@/lib/api/reports-client.js';
import { cn } from '@/lib/utils.js';
import { StatusBadge } from './status-badge.js';

interface ExportsRowProps {
  item: ExportSummary;
  statusLabel: string;
  onDownload: (exportId: string) => void;
  onCancel: (exportId: string) => void;
  isCancelling: boolean;
}

// ─── Format icons ────────────────────────────────────────────────────────────

function FormatIcon({ format }: { format: 'csv' | 'xlsx' | 'pdf' }) {
  if (format === 'csv') {
    return (
      <span
        aria-label="CSV"
        title="CSV"
        className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded bg-emerald-600/20 px-1 text-[10px] font-bold text-emerald-400"
      >
        CSV
      </span>
    );
  }
  if (format === 'xlsx') {
    return (
      <span
        aria-label="Excel"
        title="Excel"
        className="inline-flex h-5 min-w-[2.25rem] items-center justify-center rounded bg-green-700/20 px-1 text-[10px] font-bold text-green-400"
      >
        XLS
      </span>
    );
  }
  return (
    <span
      aria-label="PDF"
      title="PDF"
      className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded bg-red-700/20 px-1 text-[10px] font-bold text-[var(--color-qup-error)]"
    >
      PDF
    </span>
  );
}

// ─── Size formatter ───────────────────────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024) return `${(bytes / 1_024).toFixed(0)} KB`;
  return `${bytes} bytes`;
}

// ─── Date formatter ───────────────────────────────────────────────────────────

function fmtDate(iso: string | undefined): string {
  if (iso === undefined) return '—';
  return new Date(iso).toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ExportsRow({
  item,
  statusLabel,
  onDownload,
  onCancel,
  isCancelling,
}: ExportsRowProps) {
  const isInFlight = item.status === 'queued' || item.status === 'running';
  const isCompleted = item.status === 'completed';
  const isFailed = item.status === 'failed';

  return (
    <tr
      data-testid="exports-row"
      data-status={item.status}
      className="border-b border-[var(--color-qup-border-dark)] transition-colors even:bg-[var(--color-qup-bg-dark)]/40 hover:bg-[var(--color-qup-bg-dark)]/70"
    >
      {/* Filename + format */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <FormatIcon format={item.format} />
          <span
            className="max-w-[200px] truncate text-sm text-[var(--color-qup-text-dark)]"
            title={item.filename}
          >
            {item.filename ?? item.exportId}
          </span>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={item.status} label={statusLabel} />
      </td>

      {/* Rows / Size */}
      <td className="px-4 py-3 text-xs text-[var(--color-qup-text-muted-dark)]">
        {item.rowCount !== undefined && item.rowCount !== null
          ? `${item.rowCount.toLocaleString('es')} filas`
          : '—'}
        {item.sizeBytes !== undefined && item.sizeBytes !== null && (
          <span className="ml-1">· {formatSize(item.sizeBytes)}</span>
        )}
      </td>

      {/* Requested at */}
      <td className="px-4 py-3 text-xs text-[var(--color-qup-text-muted-dark)]">
        {fmtDate(item.requestedAt)}
      </td>

      {/* Completed at */}
      <td className="px-4 py-3 text-xs text-[var(--color-qup-text-muted-dark)]">
        {isCompleted ? fmtDate(item.completedAt) : '—'}
      </td>

      {/* Error info */}
      <td className="px-4 py-3 text-xs text-[var(--color-qup-error)]">
        {isFailed && item.error !== undefined ? (
          <span title={`${item.error.message}`} className="cursor-help underline decoration-dotted">
            {item.error.code}
          </span>
        ) : (
          '—'
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        {isCompleted && (
          <button
            type="button"
            data-testid="btn-download"
            onClick={() => onDownload(item.exportId)}
            className={cn(
              'rounded-[var(--radius-button)] px-3 py-1.5 text-xs font-medium transition-colors',
              'border border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)]',
              'hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
            )}
          >
            Descargar
          </button>
        )}
        {isInFlight && (
          <button
            type="button"
            data-testid="btn-cancel"
            disabled={isCancelling}
            onClick={() => onCancel(item.exportId)}
            className={cn(
              'rounded-[var(--radius-button)] px-3 py-1.5 text-xs font-medium transition-colors',
              'border border-[var(--color-qup-error)]/50 text-[var(--color-qup-error)]/80',
              'hover:border-[var(--color-qup-error)] hover:text-[var(--color-qup-error)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-error)]',
            )}
          >
            Cancelar
          </button>
        )}
      </td>
    </tr>
  );
}
